import type { CompanyDomain } from "src/common/company-domain/types.js";
import { getExcelTarget, type ProductKey } from "./products.js";

export interface ProductAccess {
	/** The product is switched on for this domain and usable by this user. */
	active: boolean;
	/** Not active, and nothing this user can do about it from the portal. */
	unavailable: boolean;
	/** Not shown anywhere at all — distinct from `unavailable`, which still renders a greyed-out row. */
	hidden: boolean;
}

export interface ProductAccessInput {
	companyDomain: Pick<CompanyDomain, "id" | "hasDataLayer">;
	/** The domain's EWR address, null when it has no EWR. */
	ewrUrl: string | null;
	/** From `useHasExcelReportAccess` — v7 Exopen for Excel, independent of EWR. */
	hasExcelReportAccess: boolean;
	hasCompanyAdmin: boolean;
	aiChat: { hasAccess: boolean; optedIn: boolean };
	/** Planning modules switched on for the domain — zero means Planning isn't activated. */
	planningModuleCount: number;
	/** Rollout gate — see useIntelligenceLayerVisible. False hides the product entirely, even on domains that have it. */
	intelligenceLayerVisible: boolean;
	/** The domain has a Power BI connection in Pumpstation. */
	powerBiConnected: boolean;
}

/** The single source of truth for which products a domain has — every gate in the app derives from this. */
export const getProductAccess = ({
	companyDomain,
	ewrUrl,
	hasExcelReportAccess,
	hasCompanyAdmin,
	aiChat,
	planningModuleCount,
	intelligenceLayerVisible,
	powerBiConnected,
}: ProductAccessInput): Record<ProductKey, ProductAccess> => ({
	cloudPlatform: { active: true, unavailable: false, hidden: false },
	intelligenceLayer: {
		active:
			intelligenceLayerVisible && companyDomain.hasDataLayer && hasCompanyAdmin,
		unavailable: false,
		hidden: !intelligenceLayerVisible,
	},
	planning: {
		active: planningModuleCount > 0,
		unavailable: false,
		hidden: false,
	},
	ai: {
		active: aiChat.hasAccess && aiChat.optedIn,
		unavailable: !aiChat.hasAccess,
		hidden: false,
	},
	excel: {
		active:
			getExcelTarget(companyDomain, { hasExcelReportAccess, ewrUrl }) !== null,
		unavailable: false,
		hidden: false,
	},
	powerBi: {
		active: powerBiConnected,
		unavailable: false,
		hidden: false,
	},
});
